import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { UploadRecipientInput } from '@/hooks/customer-engagement/use-campaigns';

/**
 * Client-side parser for the campaign Manual List upload.
 *
 * Accepts:
 *   - CSV (.csv, text/csv)
 *   - XLSX / XLS (.xlsx, .xls, application/vnd.openxmlformats-…)
 *
 * Format-tolerant by design: we DON'T rely on column headers. The file is read
 * as a raw grid and we pick the phone column by *looking at the values* — the
 * column with the most phone-shaped cells wins. This means headerless files,
 * mislabeled headers ("Contact #", "Cell", "WhatsApp"), or extra junk columns
 * all work without the operator having to reformat anything. A header row is
 * auto-detected (first data cell in the phone column isn't phone-shaped) and,
 * when present, used to best-effort pick first/last name + email columns.
 *
 * Returns the cleaned rows + a counts summary so the dialog can show
 * "Parsed X rows · Y phones detected · Z without a phone" before upload.
 *
 * Phone normalization (E.164 / 10-digit) happens server-side in
 * CampaignsService.uploadRecipients — here we only locate + pass through the
 * raw value, so any reasonable format reaches the normalizer.
 */

export interface ParseRecipientFileResult {
  rows: UploadRecipientInput[];
  totalRows: number;
  rowsWithPhone: number;
  rowsWithoutPhone: number;
  detectedColumns: {
    phone: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  format: 'csv' | 'xlsx';
}

const FIRST_NAME_PATTERNS = [/^first[_\s-]?name$/i, /^firstname$/i, /^first$/i, /^given[_\s-]?name$/i, /^fname$/i];
const LAST_NAME_PATTERNS = [
  /^last[_\s-]?name$/i,
  /^lastname$/i,
  /^last$/i,
  /^surname$/i,
  /^family[_\s-]?name$/i,
  /^lname$/i,
];
const EMAIL_PATTERNS = [/^e[_\s-]?mail$/i, /mail/i];

function cleanString(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/**
 * Is this cell value shaped like a phone number? Permissive on purpose — the
 * server does the real E.164 normalization. Accepts: 10 digits (US local),
 * 11 digits starting with 1 (US E.164 without +), or a leading-+ international
 * number of 10–15 digits. Strips spaces/dashes/parens/dots first.
 */
function isPhoneLike(v: string): boolean {
  const s = v.trim();
  if (!s) return false;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  if (s.startsWith('+') && digits.length >= 10 && digits.length <= 15) return true;
  return false;
}

function looksLikeEmail(v: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
}

/** Pick the column index whose cells most often look like phone numbers. */
function detectPhoneColumn(grid: string[][]): { index: number; ratio: number } {
  const colCount = grid.reduce((m, r) => Math.max(m, r.length), 0);
  let bestIndex = 0;
  let bestCount = -1;
  let bestRatio = 0;
  for (let c = 0; c < colCount; c++) {
    let phoneish = 0;
    let nonEmpty = 0;
    for (const row of grid) {
      const v = cleanString(row[c]);
      if (!v) continue;
      nonEmpty += 1;
      // Don't let an email column ("123@..." edge cases) masquerade as phone.
      if (!looksLikeEmail(v) && isPhoneLike(v)) phoneish += 1;
    }
    if (phoneish > bestCount) {
      bestCount = phoneish;
      bestIndex = c;
      bestRatio = nonEmpty > 0 ? phoneish / nonEmpty : 0;
    }
  }
  return { index: bestIndex, ratio: bestRatio };
}

/** Match a header cell against name/email patterns to find optional columns. */
function pickHeaderColumn(header: string[], patterns: RegExp[]): number | null {
  for (const pat of patterns) {
    const idx = header.findIndex((h) => pat.test(cleanString(h)));
    if (idx >= 0) return idx;
  }
  return null;
}

function buildResult(grid: string[][], format: 'csv' | 'xlsx'): ParseRecipientFileResult {
  const nonEmptyRows = grid.filter((r) => r.some((c) => cleanString(c) !== ''));
  if (nonEmptyRows.length === 0) {
    return {
      rows: [],
      totalRows: 0,
      rowsWithPhone: 0,
      rowsWithoutPhone: 0,
      detectedColumns: { phone: null, firstName: null, lastName: null, email: null },
      format,
    };
  }

  const phone = detectPhoneColumn(nonEmptyRows);

  // Header detection: if the very first row's phone-column cell is NOT phone-
  // shaped but a later row's is, row 0 is a header label row. If row 0 is
  // already phone-shaped, the file has no header and every row is data.
  const firstPhoneCell = cleanString(nonEmptyRows[0]?.[phone.index]);
  const laterHasPhone = nonEmptyRows.slice(1).some((r) => isPhoneLike(cleanString(r[phone.index])));
  const hasHeader = !isPhoneLike(firstPhoneCell) && laterHasPhone;

  const header = hasHeader ? nonEmptyRows[0].map(cleanString) : [];
  const dataRows = hasHeader ? nonEmptyRows.slice(1) : nonEmptyRows;

  // Optional columns only resolvable when there's a header to read.
  const firstNameIdx = hasHeader ? pickHeaderColumn(header, FIRST_NAME_PATTERNS) : null;
  const lastNameIdx = hasHeader ? pickHeaderColumn(header, LAST_NAME_PATTERNS) : null;
  let emailIdx = hasHeader ? pickHeaderColumn(header, EMAIL_PATTERNS) : null;
  if (emailIdx === phone.index) emailIdx = null; // never alias the phone column

  const rows: UploadRecipientInput[] = [];
  let withPhone = 0;
  let withoutPhone = 0;
  for (const r of dataRows) {
    const phoneVal = cleanString(r[phone.index]);
    if (!phoneVal) {
      withoutPhone += 1;
      continue;
    }
    withPhone += 1;
    rows.push({
      phone: phoneVal,
      firstName: firstNameIdx != null ? cleanString(r[firstNameIdx]) || undefined : undefined,
      lastName: lastNameIdx != null ? cleanString(r[lastNameIdx]) || undefined : undefined,
      email: emailIdx != null ? cleanString(r[emailIdx]) || undefined : undefined,
    });
  }

  const label = (idx: number | null): string | null =>
    idx == null ? null : hasHeader && header[idx] ? header[idx] : `Column ${idx + 1}`;

  return {
    rows,
    totalRows: dataRows.length,
    rowsWithPhone: withPhone,
    rowsWithoutPhone: withoutPhone,
    detectedColumns: {
      phone: label(phone.index),
      firstName: label(firstNameIdx),
      lastName: label(lastNameIdx),
      email: label(emailIdx),
    },
    format,
  };
}

export async function parseRecipientFile(file: File): Promise<ParseRecipientFileResult> {
  const name = file.name.toLowerCase();
  const isExcel = /\.(xlsx|xls)$/.test(name) || /spreadsheet|excel/.test(file.type);

  if (isExcel) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) {
      return {
        rows: [],
        totalRows: 0,
        rowsWithPhone: 0,
        rowsWithoutPhone: 0,
        detectedColumns: { phone: null, firstName: null, lastName: null, email: null },
        format: 'xlsx',
      };
    }
    const sheet = wb.Sheets[sheetName];
    // header:1 → raw array-of-arrays (no header assumption).
    const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false }) as unknown[][];
    return buildResult(
      grid.map((r) => r.map(cleanString)),
      'xlsx',
    );
  }

  // CSV path — parse to raw rows (no header assumption) for content detection.
  return new Promise<ParseRecipientFileResult>((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (result) => {
        if (result.errors.length > 0) {
          const fatal = result.errors.find((e) => e.type === 'Quotes' || e.type === 'FieldMismatch');
          if (fatal) {
            reject(new Error(`CSV parse error: ${fatal.message}`));
            return;
          }
        }
        const grid = (result.data as unknown[][]).map((r) => (Array.isArray(r) ? r.map(cleanString) : []));
        resolve(buildResult(grid, 'csv'));
      },
      error: (err: Error) => reject(err),
    });
  });
}
